import { useEffect, useState } from 'react';

const NATIVE_EMBED_LOADING_ATTRIBUTE = 'data-native-embed-loading';

const getObservedElement = (
	container: HTMLElement | null,
	innerContainer?: HTMLElement | null,
): HTMLElement | null => {
	if (innerContainer?.hasAttribute(NATIVE_EMBED_LOADING_ATTRIBUTE)) {
		return innerContainer;
	}

	return container?.querySelector<HTMLElement>(`[${NATIVE_EMBED_LOADING_ATTRIBUTE}]`) ?? null;
};

const readLoadingState = (
	container: HTMLElement | null,
	innerContainer?: HTMLElement | null,
): boolean => {
	return (
		getObservedElement(container, innerContainer)?.getAttribute(
			NATIVE_EMBED_LOADING_ATTRIBUTE,
		) === 'true'
	);
};

export const useNativeEmbedLoadingState = (
	container: HTMLElement | null,
	enabled: boolean,
	innerContainer?: HTMLElement | null,
): boolean => {
	const observedElement = getObservedElement(container, innerContainer);
	const [isLoading, setIsLoading] = useState(() =>
		enabled ? readLoadingState(container, innerContainer) : false,
	);

	useEffect(() => {
		if (!enabled || !observedElement || typeof MutationObserver === 'undefined') {
			setIsLoading(false);
			return;
		}

		const updateLoadingState = () => {
			setIsLoading(
				observedElement.getAttribute(NATIVE_EMBED_LOADING_ATTRIBUTE) === 'true',
			);
		};

		updateLoadingState();

		const observer = new MutationObserver(updateLoadingState);
		observer.observe(observedElement, {
			attributes: true,
			attributeFilter: [NATIVE_EMBED_LOADING_ATTRIBUTE],
		});

		return () => {
			observer.disconnect();
		};
	}, [enabled, observedElement]);

	return isLoading;
};
